import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { logger } from '../../utils/logger';
import { supabase } from '../../utils/supabase';
import Button from '../ui/Button';
import { calculateMatchScore } from '../../services/candidate-service';
import { getSafeSkillName } from '../../utils/skillsUtils';

const JobCard = ({ job, matchScore: passedMatchScore, _onApply, onViewDetails, onBookmark }) => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isBookmarking, setIsBookmarking] = useState(false);
  const [calculatedMatchScore, setCalculatedMatchScore] = useState(passedMatchScore || 0);

  // Handle view details - navigate to job details page
  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(job); // Pass entire job object, not just ID
    } else {
      navigate(`/job-details/${job.id}`);
    }
  };

  // Handle bookmark toggle
  const handleBookmark = async (event) => {
    event.stopPropagation(); // Prevent card click event
    
    if (!user) {
      navigate('/login');
      return;
    }

    if (onBookmark) {
      onBookmark(job.id, isBookmarked); // Pass jobId and current bookmark state
      setIsBookmarked(!isBookmarked); // Toggle local state
      return;
    }

    setIsBookmarking(true);

    try {
      if (isBookmarked) {
        // Remove bookmark
        const { error } = await supabase
          .from('job_bookmarks')
          .delete()
          .eq('job_id', job.id)
          .eq('user_id', user.id);

        if (error) throw error;
        setIsBookmarked(false);
      } else {
        // Add bookmark
        const { error } = await supabase
          .from('job_bookmarks')
          .insert({
            job_id: job.id,
            user_id: user.id,
            created_at: new Date().toISOString()
          });

        if (error) throw error;
        setIsBookmarked(true);
      }
    } catch (error) {
      logger.error('Error bookmarking job', error);
    } finally {
      setIsBookmarking(false);
    }
  };

  // Check if job is bookmarked on component mount
  useEffect(() => {
    if (user && job.id) {
      const checkBookmark = async () => {
        try {
          const { data, error } = await supabase
            .from('job_bookmarks')
            .select('id')
            .eq('job_id', job.id)
            .eq('user_id', user.id);

          if (!error && data && data.length > 0) {
            setIsBookmarked(true);
          } else {
            setIsBookmarked(false);
          }
        } catch {
          // Ignore error if no bookmark exists
          setIsBookmarked(false);
        }
      };

      checkBookmark();
    }
  }, [user, job.id]);

  // Calculate match score dynamically for consistency
  useEffect(() => {
    // First, check if we have a pre-calculated matchScore passed in
    if (passedMatchScore !== undefined && passedMatchScore > 0) {
      setCalculatedMatchScore(passedMatchScore);
      return;
    }

    // Calculate match score based on user profile skills
    if (!userProfile?.skills) {
      setCalculatedMatchScore(0);
      return;
    }

    // Prepare candidate object with normalized skills
    const candidate = {
      skills: userProfile.skills.map(skill => getSafeSkillName(skill)),
      experience_years: userProfile?.experience_years || 0
    };

    // Prepare job object with normalized skills
    const jobData = {
      skills: (job?.skills_required || job?.required_skills || job?.skills || []).map(skill => {
        return typeof skill === 'string' ? skill : skill?.name || '';
      }).filter(s => s) // Remove empty strings
    };

    // Check if there's at least one matching skill
    const hasMatchingSkill = jobData.skills.length > 0 && 
      jobData.skills.some(jobSkill => 
        candidate.skills.some(candidateSkill => 
          candidateSkill.toLowerCase() === jobSkill.toLowerCase()
        )
      );

    // Only calculate match score if there's at least one matching skill
    if (!hasMatchingSkill) {
      setCalculatedMatchScore(0);
      return;
    }

    // Calculate and set match score
    const score = calculateMatchScore(candidate, jobData);
    setCalculatedMatchScore(score);
  }, [job, userProfile, passedMatchScore]);

  const displayRate = () => {
    if (job.hourly_rate_min && job.hourly_rate_max) {
      return `$${job.hourly_rate_min}-$${job.hourly_rate_max}/hr`;
    } else if (job.hourly_rate_min) {
      return `$${job.hourly_rate_min}/hr`;
    } else if (job.hourly_rate) {
      return `$${job.hourly_rate}/hr`;
    } else if (job.budget_min && job.budget_max) {
      return `$${job.budget_min}-$${job.budget_max}`;
    } else if (job.budget_min) {
      return `$${job.budget_min}`;
    } else if (job.rate) {
      return `$${job.rate}${job.rateType === 'hourly' ? '/hr' : ''}`;
    } else {
      return 'Rate not specified';
    }
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
      onClick={handleViewDetails}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-lg text-gray-900 mb-1">
            {job.title}
          </h3>
          <p className="text-gray-600 text-sm">
            {job.user_profiles?.company_name || job.company_name || job.companyName || job.company || 'Company Name'}
          </p>
        </div>
        {calculatedMatchScore && (
          <div className={`px-2 py-1 mr-2 rounded-full text-xs font-medium ${
            calculatedMatchScore >= 80 ? 'bg-green-100 text-green-800' : 
            calculatedMatchScore >= 60 ? 'bg-blue-100 text-blue-800' : 
            'bg-gray-100 text-gray-800'
          }`}>
            {calculatedMatchScore}% match
          </div>
        )}
        <button
          className={`${
            isBookmarked 
              ? 'text-blue-500 hover:text-blue-600' 
              : 'text-gray-400 hover:text-blue-500'
          } ${isBookmarking ? 'opacity-50 cursor-not-allowed' : ''}`}
          onClick={handleBookmark}
          disabled={isBookmarking}
          title={isBookmarked ? 'Remove from bookmarks' : 'Add to bookmarks'}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill={isBookmarked ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
            />
          </svg>
        </button>
      </div>

      {/* Job Details */}
      <div className="space-y-2 mb-4 flex flex-row gap-2 items-center">
        <div className="flex items-center mt-2 text-sm text-gray-600">
          <span className="mr-1 font-semibold">Location:</span>
          {job.location || 'Location not specified'}
          {job.is_remote && <span className="ml-2 text-green-600 text-xs">(Remote)</span>}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <span className="mr-1 font-semibold">Rate:</span>
          {displayRate()}
        </div>
        {job.job_type && (
          <div className="flex items-center text-sm text-gray-600">
            <span className="mr-1 font-semibold">Availability:</span>
            {job.job_type}
          </div>
        )}
      </div>

       {/* Description Preview */}
      <div className="mb-4">
        <p className="text-gray-700 text-sm line-clamp-2">
          {job.description}
        </p>
      </div>

      {/* Recommendation Reasons */}
      {job.recommendationReasons && job.recommendationReasons.length > 0 && (
        <div className="mb-4">
          {job.recommendationReasons.map((reason, index) => (
            <div key={index} className="flex items-center text-xs text-primary-700 bg-primary-50 rounded-full px-3 py-1 mb-2 inline-block mr-2">
              <span className="mr-1">✓</span>
              {reason}
            </div>
          ))}
        </div>
      )}

      {/* Match Reason */}
      {job.match_reason && (
        <div className="mb-3">
          <p className="text-xs text-blue-600 italic">
            {job.match_reason}
          </p>
        </div>
      )}
      {/* Skills */}
      {job.required_skills && job.required_skills.length > 0 ? (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {job.required_skills.slice(0, 3).map((skill, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
              >
                {skill}
              </span>
            ))}
            {job.required_skills.length > 3 && (
              <span className="text-gray-500 text-xs flex items-center">
                +{job.required_skills.length - 3} more
              </span>
            )}
          </div>
        </div>
      ) : job.skills && job.skills.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {job.skills.slice(0, 3).map((skill, index) => (
              <span
                key={index}
                className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
              >
                {skill}
              </span>
            ))}
            {job.skills.length > 3 && (
              <span className="text-gray-500 text-xs flex items-center">
                +{job.skills.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}
      
      
      {/* Posted Time */}
      {job.postedTime && (
        <p className="text-xs text-gray-500 mt-2">
          Posted {job.postedTime}
        </p>
      )}
    </div>
  );
};

export default JobCard;
